import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, GraduationCap, Loader2 } from 'lucide-react';
import type { LegacyRecognitionDecisionInput } from '@lms/shared/types';
import { apiClient } from '../lib/api-client';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

export function ParticipantPriorLearningRecognition({ enrollmentId }: { enrollmentId: string }) {
  const { language, t } = useLanguage();
  const prefix = useId();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [transcriptId, setTranscriptId] = useState('');
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [verified, setVerified] = useState(false);
  const [limit, setLimit] = useState(5);
  const [notice, setNotice] = useState('');
  const words = copy.priorLearning;
  const report = useQuery({
    queryKey: ['prior-learning', enrollmentId],
    queryFn: () => apiClient.priorLearning.report(enrollmentId),
    enabled: expanded,
  });
  const subjects = report.data?.subjects ?? [];
  const selected = subjects.find(subject => subject.id === subjectId);
  const candidates = report.data?.candidates ?? [];
  const candidate = candidates.find(item => item.id === transcriptId);
  const filtered = subjects.filter(subject =>
    `${subject.name} ${subject.code ?? ''} ${subject.location}`
      .toLocaleLowerCase()
      .includes(search.trim().toLocaleLowerCase())
  );
  const mutation = useMutation({
    mutationFn: ({
      action,
      input,
    }: {
      action: 'approve' | 'revoke';
      input: LegacyRecognitionDecisionInput;
    }) => apiClient.priorLearning.decide(enrollmentId, subjectId, action, input),
    onSuccess: result => {
      queryClient.setQueryData(['prior-learning', enrollmentId], result.report);
      for (const key of ['enrollments', 'users', 'programs', 'evaluations', 'academic-transcripts'])
        void queryClient.invalidateQueries({ queryKey: [key] });
      setNotice(t(result.progressSynced ? words.saved : words.syncPending));
      setReason('');
      setConfirmation('');
      setVerified(false);
    },
    onError: () => {
      void report.refetch();
    },
  });
  const submit = (action: 'approve' | 'revoke') => {
    if (!selected) return;
    setNotice('');
    mutation.mutate({
      action,
      input: {
        transcriptId: action === 'approve' ? transcriptId : undefined,
        reason: reason.trim(),
        confirmation: action === 'approve' ? 'SAHKAN' : 'CABUT',
        expectedUpdatedAt: selected.decision?.updatedAt ?? null,
        ...(action === 'approve' ? { verifiedSource: verified } : {}),
      },
    });
  };
  const controlClass =
    'w-full min-h-12 rounded-xl bg-surface-container-high px-4 py-3 text-base text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
  const busy = mutation.isPending;
  const validReason = reason.trim().length >= 5 && reason.length <= 1000;
  const canApprove =
    selected &&
    !selected.reset &&
    selected.materialCount > 0 &&
    candidate &&
    !candidate.block &&
    candidate.score !== null &&
    candidate.score >= selected.passingScore &&
    verified &&
    validReason &&
    confirmation === 'SAHKAN';
  const canRevoke =
    selected &&
    (selected.recognition || selected.decision?.active) &&
    validReason &&
    confirmation === 'CABUT';
  return (
    <section className="rounded-2xl bg-surface-container-low p-4 text-base text-on-surface sm:p-5">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`${prefix}-panel`}
        onClick={() => setExpanded(!expanded)}
        className="flex min-h-12 w-full items-center justify-between gap-3 text-start font-headline font-bold focus-visible:outline-2 focus-visible:outline-primary"
      >
        <span className="flex items-center gap-2">
          <GraduationCap aria-hidden="true" size={22} />
          {t(words.title)}
        </span>
        <ChevronDown aria-hidden="true" className={expanded ? 'rotate-180' : ''} />
      </button>
      {expanded && (
        <div id={`${prefix}-panel`} className="mt-4 space-y-5">
          <p className="leading-relaxed text-on-surface-variant">{t(words.intro)}</p>
          {report.isLoading && (
            <p role="status" className="flex items-center gap-2">
              <Loader2 className="animate-spin" size={18} />
              {t(words.loading)}
            </p>
          )}
          {report.isError && (
            <div role="alert">
              <p>{t(words.loadError)}</p>
              <button
                type="button"
                onClick={() => void report.refetch()}
                className="min-h-12 text-primary font-bold"
              >
                {t(words.retry)}
              </button>
            </div>
          )}
          {report.data && (
            <>
              <div>
                <label htmlFor={`${prefix}-search`} className="mb-2 block font-semibold">
                  {t(words.search)}
                </label>
                <input
                  id={`${prefix}-search`}
                  value={search}
                  disabled={busy}
                  onChange={event => {
                    setSearch(event.target.value);
                    setLimit(5);
                    setSubjectId('');
                    setTranscriptId('');
                    setConfirmation('');
                    setVerified(false);
                    setReason('');
                  }}
                  className={controlClass}
                />
              </div>
              <div className="space-y-3">
                {filtered.slice(0, limit).map(subject => (
                  <article key={subject.id} className="space-y-2 rounded-xl bg-surface p-4">
                    <p className="font-bold break-words">
                      {subject.name}{' '}
                      {subject.code && <span className="font-mono text-sm">({subject.code})</span>}
                    </p>
                    <p className="text-sm text-on-surface-variant">{subject.location}</p>
                    <p
                      className={
                        subject.recognition
                          ? 'font-semibold text-primary'
                          : 'text-on-surface-variant'
                      }
                    >
                      {subject.reset
                        ? t(words.reset)
                        : subject.recognition
                          ? `${t(subject.recognition.method === 'ADMIN' ? words.manual : words.automatic)} · ${subject.recognition.score}%`
                          : subject.decision && !subject.decision.active
                            ? t(words.revoked)
                            : subject.materialCount === 0
                              ? t(words.noMaterials)
                              : t(words.unmatched)}
                    </p>
                    {subject.recognition && (
                      <p className="break-words text-sm">{subject.recognition.sourceTitle}</p>
                    )}
                    {subject.decision && (
                      <div className="rounded-lg bg-surface-container-high p-3 text-sm">
                        <p>
                          {t(words.history)
                            .replace(
                              '{date}',
                              new Date(subject.decision.updatedAt).toLocaleString(
                                language === 'ar' ? 'ar' : 'id-ID'
                              )
                            )
                            .replace('{admin}', subject.decision.decidedBy)}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap break-words">
                          {subject.decision.reason}
                        </p>
                      </div>
                    )}
                  </article>
                ))}
                {filtered.length === 0 && <p>{t(words.empty)}</p>}
                {filtered.length > limit && (
                  <button
                    type="button"
                    onClick={() => setLimit(limit + 5)}
                    className="min-h-12 font-semibold text-primary"
                  >
                    {t(words.showMore)}
                  </button>
                )}
              </div>
              {candidates.filter(source => !source.block).length === 0 && (
                <p className="rounded-xl bg-secondary-container p-4 text-on-secondary-container">
                  {t(words.noSources)}
                </p>
              )}
              <div>
                <label htmlFor={`${prefix}-subject`} className="mb-2 block font-semibold">
                  {t(words.subject)}
                </label>
                <select
                  id={`${prefix}-subject`}
                  value={subjectId}
                  disabled={busy}
                  onChange={event => {
                    setSubjectId(event.target.value);
                    setTranscriptId('');
                    setSourceSearch('');
                    setReason('');
                    setNotice('');
                    setConfirmation('');
                    setVerified(false);
                    mutation.reset();
                  }}
                  className={controlClass}
                >
                  <option value="">{t(words.choose)}</option>
                  {filtered.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name} {subject.code ?? ''} — {subject.location}
                    </option>
                  ))}
                </select>
              </div>
              {selected && (
                <div className="space-y-4 rounded-xl bg-surface p-4">
                  <p className="text-sm leading-relaxed">
                    {t(words.sourceHint).replace('{score}', String(selected.passingScore))}
                  </p>
                  {candidate?.requiresGradeVerification && (
                    <p className="rounded-xl bg-secondary-container p-3 leading-relaxed text-on-secondary-container">
                      {t(words.estimatedWarning)}
                    </p>
                  )}
                  {candidate?.sourceCourseFinished === false && (
                    <p className="rounded-xl bg-surface-container-high p-3 leading-relaxed text-on-surface-variant">
                      {t(words.unfinishedSourceWarning)}
                    </p>
                  )}
                  {selected.reset && (
                    <p role="status" className="text-on-warning-container">
                      {t(words.reset)}
                    </p>
                  )}
                  <div>
                    <label htmlFor={`${prefix}-source-search`} className="mb-2 block font-semibold">
                      {t(words.sourceSearch)}
                    </label>
                    <input
                      id={`${prefix}-source-search`}
                      value={sourceSearch}
                      disabled={busy}
                      onChange={event => {
                        setSourceSearch(event.target.value);
                        setTranscriptId('');
                        setVerified(false);
                      }}
                      className={controlClass}
                    />
                  </div>
                  <div>
                    <label htmlFor={`${prefix}-source`} className="mb-2 block font-semibold">
                      {t(words.source)}
                    </label>
                    <select
                      id={`${prefix}-source`}
                      value={transcriptId}
                      disabled={busy || selected.reset}
                      onChange={event => {
                        setTranscriptId(event.target.value);
                        setVerified(false);
                      }}
                      className={controlClass}
                    >
                      <option value="">{t(words.chooseSource)}</option>
                      {candidates
                        .filter(source =>
                          `${source.title} ${source.subjectName}`
                            .toLocaleLowerCase()
                            .includes(sourceSearch.trim().toLocaleLowerCase())
                        )
                        .map(source => (
                          <option
                            key={source.id}
                            value={source.id}
                            disabled={
                              Boolean(source.block) ||
                              source.score === null ||
                              source.score < selected.passingScore
                            }
                          >
                            {source.title} — {source.score ?? '—'}%{' '}
                            {source.block ? `(${t(words.blocks[source.block])})` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                  <label className="flex items-start gap-3 leading-relaxed">
                    <input
                      type="checkbox"
                      checked={verified}
                      onChange={event => setVerified(event.target.checked)}
                      disabled={busy}
                      className="mt-1 h-5 w-5 shrink-0 accent-primary"
                    />
                    <span>{t(words.verify)}</span>
                  </label>
                  <div>
                    <label htmlFor={`${prefix}-reason`} className="mb-2 block font-semibold">
                      {t(words.reason)}
                    </label>
                    <textarea
                      id={`${prefix}-reason`}
                      value={reason}
                      maxLength={1000}
                      disabled={busy}
                      onChange={event => setReason(event.target.value)}
                      className={`${controlClass} min-h-28`}
                    />
                  </div>
                  <div>
                    <label htmlFor={`${prefix}-confirmation`} className="mb-2 block font-semibold">
                      {t(words.confirmation).replace('{word}', 'SAHKAN / CABUT')}
                    </label>
                    <input
                      id={`${prefix}-confirmation`}
                      value={confirmation}
                      disabled={busy}
                      autoComplete="off"
                      onChange={event => setConfirmation(event.target.value)}
                      className={controlClass}
                    />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={busy || !canApprove}
                      onClick={() => submit('approve')}
                      className="min-h-12 rounded-xl bg-primary px-5 py-3 font-bold text-on-primary disabled:opacity-50"
                    >
                      {t(words.approve)}
                    </button>
                    <button
                      type="button"
                      disabled={busy || !canRevoke}
                      onClick={() => submit('revoke')}
                      className="min-h-12 rounded-xl bg-error-container px-5 py-3 font-bold text-on-error-container disabled:opacity-50"
                    >
                      {t(words.revoke)}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {mutation.isError && (
            <p role="alert" className="rounded-xl bg-error-container p-4 text-on-error-container">
              {mutation.error.message}
            </p>
          )}
          {notice && (
            <p role="status" className="rounded-xl bg-success-container p-4 text-primary">
              {notice}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
